import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  ErrorText,
  FieldLabel,
  LoadingView,
  PrimaryButton,
  SBCard,
  SecondaryButton,
} from "@/components/ui";
import {
  SUPPORT_GRANT_DURATION_OPTIONS,
  SUPPORT_MESSAGE_MAX_LENGTH,
  SUPPORT_NOTE_MAX_LENGTH,
  SUPPORT_SUBJECT_MAX_LENGTH,
  activeSupportGrants,
  openSupportRequests,
  pendingSupportGrants,
  resolvedSupportRequests,
  type SupportAccessGrant,
  type SupportRequest,
  type SupportWorkspace,
} from "@/models/supportRequest";
import { USER_ROLE_LABELS, preferredName, type SBotUserRole } from "@/models/user";
import {
  addAdminSupportReply,
  addOwnSupportMessage,
  createSupportRequest,
  decideSupportAccessGrant,
  getAdminSupportRequestDetail,
  listAdminSupportRequests,
  listOwnSupportRequests,
  requestAdminSupportAccess,
  resolveOwnSupportRequest,
  revokeSupportAccessGrant,
  setAdminSupportRequestResolved,
} from "@/services/supportRequests";
import "@/features/home/home.css";
import "./support.css";

export function UserSupportView() {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [grants, setGrants] = useState<SupportAccessGrant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingGrant, setIsSubmittingGrant] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const inbox = await listOwnSupportRequests();
      setRequests(inbox.requests);
      setGrants(inbox.grants);
    } catch (cause) {
      setError(errorMessage(cause, "Support requests could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceGrant = (grant: SupportAccessGrant) => {
    setGrants((current) => [grant, ...current.filter((candidate) => candidate.id !== grant.id)]);
  };

  const decide = async (grant: SupportAccessGrant, decision: "approve" | "decline") => {
    if (
      decision === "approve"
      && !window.confirm(
        `${grant.adminName} will receive your ${workspaceRoleLabel(grant.workspace)} role in `
        + `${grant.workspace.displayName} for ${grant.durationDays} day${grant.durationDays === 1 ? "" : "s"}. Approve access?`,
      )
    ) {
      return;
    }

    setIsSubmittingGrant(true);
    setError(null);
    try {
      replaceGrant(await decideSupportAccessGrant(grant.supportRequestID, grant.id, decision));
    } catch (cause) {
      setError(errorMessage(cause, "The access decision could not be saved."));
    } finally {
      setIsSubmittingGrant(false);
    }
  };

  const revoke = async (grant: SupportAccessGrant) => {
    if (!window.confirm(`${grant.adminName} will immediately lose access to ${grant.workspace.displayName}. Revoke access?`)) {
      return;
    }
    setIsSubmittingGrant(true);
    setError(null);
    try {
      replaceGrant(await revokeSupportAccessGrant(grant.supportRequestID, grant.id));
    } catch (cause) {
      setError(errorMessage(cause, "Support access could not be revoked."));
    } finally {
      setIsSubmittingGrant(false);
    }
  };

  const pending = pendingSupportGrants(grants);
  const active = activeSupportGrants(grants);
  const open = openSupportRequests(requests);
  const resolved = resolvedSupportRequests(requests);

  return (
    <div className="list-screen support-screen">
      <div className="list-screen-head">
        <div>
          <p className="home-eyebrow">Account</p>
          <h1>Request Support</h1>
        </div>
        <SecondaryButton title="Refresh" onClick={() => void load()} disabled={isLoading} />
      </div>

      <SBCard className="home-card support-intro">
        <h3>Support</h3>
        <p className="home-hint">
          Send a message to the SBot team. Your name, email, and active workspace are attached so we can help faster.
        </p>
        <ErrorText>{error}</ErrorText>
      </SBCard>

      {pending.map((grant) => (
        <PendingGrantCard
          key={grant.id}
          grant={grant}
          disabled={isSubmittingGrant}
          onApprove={() => void decide(grant, "approve")}
          onDecline={() => void decide(grant, "decline")}
        />
      ))}

      {active.map((grant) => (
        <ActiveGrantCard
          key={grant.id}
          grant={grant}
          disabled={isSubmittingGrant}
          onRevoke={() => void revoke(grant)}
        />
      ))}

      {!showNewRequest ? (
        <PrimaryButton title="New Support Request" onClick={() => setShowNewRequest(true)} />
      ) : (
        <NewSupportRequestForm
          currentUser={currentUser}
          onCancel={() => setShowNewRequest(false)}
          onCreated={(request) => {
            setRequests((current) => [request, ...current.filter((candidate) => candidate.id !== request.id)]);
            setShowNewRequest(false);
          }}
        />
      )}

      {isLoading && requests.length === 0 ? (
        <SBCard><LoadingView label="Loading requests…" /></SBCard>
      ) : requests.length === 0 ? (
        <SBCard className="home-card">
          <h3>No requests yet</h3>
          <p className="home-hint">Your support conversations will appear here.</p>
        </SBCard>
      ) : (
        <>
          <SupportRequestSection title="Open" requests={open} basePath="/account/support" showRequester={false} />
          <SupportRequestSection title="Resolved" requests={resolved} basePath="/account/support" showRequester={false} />
        </>
      )}
    </div>
  );
}

export function UserSupportThreadView() {
  const { requestID = "" } = useParams();
  const [request, setRequest] = useState<SupportRequest | null>(null);
  const [reply, setReply] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const inbox = await listOwnSupportRequests();
      const match = inbox.requests.find((candidate) => candidate.id === requestID);
      if (!match) throw new Error("This support request was not found in your account.");
      setRequest(match);
    } catch (cause) {
      setError(errorMessage(cause, "Support request could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, [requestID]);

  useEffect(() => {
    void load();
  }, [load]);

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    const body = reply.trim();
    if (!request || !body) return;
    setIsSubmitting(true);
    setError(null);
    try {
      setRequest(await addOwnSupportMessage(request.id, body));
      setReply("");
    } catch (cause) {
      setError(errorMessage(cause, "Your reply could not be sent."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolve = async () => {
    if (!request) return;
    setIsSubmitting(true);
    setError(null);
    try {
      setRequest(await resolveOwnSupportRequest(request.id));
    } catch (cause) {
      setError(errorMessage(cause, "The request could not be resolved."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !request) return <LoadingView label="Loading request…" />;

  return (
    <div className="list-screen support-screen">
      <div className="support-back-row">
        <Link to="/account/support">← Request Support</Link>
        <SecondaryButton title="Refresh" onClick={() => void load()} disabled={isLoading} />
      </div>
      <ErrorText>{error}</ErrorText>
      {request && (
        <>
          <SupportThreadCard request={request} />
          <SupportReplyComposer
            label="Write a reply"
            value={reply}
            setValue={setReply}
            onSubmit={sendReply}
            disabled={isSubmitting}
            buttonTitle="Send"
          />
          {request.status === "open" && (
            <SecondaryButton title="Mark as resolved" onClick={() => void resolve()} disabled={isSubmitting} />
          )}
        </>
      )}
    </div>
  );
}

export function AdminSupportInboxView() {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setRequests(await listAdminSupportRequests());
    } catch (cause) {
      setError(errorMessage(cause, "Support inbox could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="list-screen support-screen">
      <div className="list-screen-head">
        <div>
          <p className="home-eyebrow">SBot Labs Admin</p>
          <h1>Support Inbox</h1>
        </div>
        <SecondaryButton title="Refresh" onClick={() => void load()} disabled={isLoading} />
      </div>
      <SBCard className="home-card support-intro">
        <h3>Support Inbox</h3>
        <p className="home-hint">
          Read and reply to user requests, and ask for temporary account access when you need to see what they see.
        </p>
        <ErrorText>{error}</ErrorText>
      </SBCard>
      {isLoading && requests.length === 0 ? (
        <SBCard><LoadingView label="Loading requests…" /></SBCard>
      ) : requests.length === 0 ? (
        <SBCard className="home-card">
          <h3>No support requests</h3>
          <p className="home-hint">New requests will appear here and by email.</p>
        </SBCard>
      ) : (
        <>
          <SupportRequestSection
            title="Open"
            requests={openSupportRequests(requests)}
            basePath="/admin/support"
            showRequester
          />
          <SupportRequestSection
            title="Resolved"
            requests={resolvedSupportRequests(requests)}
            basePath="/admin/support"
            showRequester
          />
        </>
      )}
    </div>
  );
}

export function AdminSupportDetailView() {
  const { currentUser } = useAuth();
  const { requestID = "" } = useParams();
  const [request, setRequest] = useState<SupportRequest | null>(null);
  const [grants, setGrants] = useState<SupportAccessGrant[]>([]);
  const [reply, setReply] = useState("");
  const [durationDays, setDurationDays] = useState<number>(3);
  const [note, setNote] = useState("");
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const detail = await getAdminSupportRequestDetail(requestID);
      setRequest(detail.request);
      setGrants(detail.grants);
    } catch (cause) {
      setError(errorMessage(cause, "Support request could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, [requestID]);

  useEffect(() => {
    void load();
  }, [load]);

  const currentGrant = useMemo(
    () => grants.find((grant) => grant.requestingAdminUserID === currentUser?.id),
    [currentUser?.id, grants],
  );
  const canRequestAccess = !currentGrant
    || currentGrant.status === "declined"
    || currentGrant.status === "revoked"
    || currentGrant.status === "expired";

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    const body = reply.trim();
    if (!request || !body) return;
    setIsSubmitting(true);
    setError(null);
    try {
      setRequest(await addAdminSupportReply(request.id, body));
      setReply("");
    } catch (cause) {
      setError(errorMessage(cause, "The reply could not be sent."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const setResolved = async (resolved: boolean) => {
    if (!request) return;
    setIsSubmitting(true);
    setError(null);
    try {
      setRequest(await setAdminSupportRequestResolved(request.id, resolved));
    } catch (cause) {
      setError(errorMessage(cause, "The request status could not be changed."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestAccess = async (event: FormEvent) => {
    event.preventDefault();
    if (!request) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const grant = await requestAdminSupportAccess(request.id, durationDays, note.trim() || undefined);
      setGrants((current) => [grant, ...current.filter((candidate) => candidate.id !== grant.id)]);
      setNote("");
      setShowAccessForm(false);
    } catch (cause) {
      setError(errorMessage(cause, "The access request could not be sent."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !request) return <LoadingView label="Loading request…" />;

  return (
    <div className="list-screen support-screen">
      <div className="support-back-row">
        <Link to="/admin/support">← Support Inbox</Link>
        <SecondaryButton title="Refresh" onClick={() => void load()} disabled={isLoading} />
      </div>
      <ErrorText>{error}</ErrorText>
      {request && (
        <>
          <SupportThreadCard request={request} />
          <SupportReplyComposer
            label={`Reply to ${request.name}`}
            value={reply}
            setValue={setReply}
            onSubmit={sendReply}
            disabled={isSubmitting}
            buttonTitle="Send Reply"
          />
          <SBCard className="home-card">
            <div className="home-card-head">
              <h3>Account Access</h3>
              {currentGrant && <SupportStatusBadge status={currentGrant.status} />}
            </div>
            {request.workspace ? (
              <p className="home-hint">Workspace on this request: {workspaceSummary(request.workspace)}</p>
            ) : (
              <p className="home-hint">This request has no workspace attached, so access cannot be requested.</p>
            )}
            {currentGrant && <GrantStatusDetails grant={currentGrant} />}
            {request.workspace && canRequestAccess && !showAccessForm && (
              <SecondaryButton
                title="Request access"
                onClick={() => setShowAccessForm(true)}
                disabled={isSubmitting}
              />
            )}
            {request.workspace && showAccessForm && (
              <form className="support-form" onSubmit={requestAccess}>
                <FieldLabel label="Duration">
                  <select value={durationDays} onChange={(event) => setDurationDays(Number(event.target.value))}>
                    {SUPPORT_GRANT_DURATION_OPTIONS.map((days) => (
                      <option key={days} value={days}>{days} day{days === 1 ? "" : "s"}</option>
                    ))}
                  </select>
                </FieldLabel>
                <FieldLabel label="Note for the user (optional)">
                  <textarea
                    value={note}
                    maxLength={SUPPORT_NOTE_MAX_LENGTH}
                    rows={3}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </FieldLabel>
                <div className="support-actions">
                  <SecondaryButton title="Cancel" onClick={() => setShowAccessForm(false)} disabled={isSubmitting} />
                  <PrimaryButton title={isSubmitting ? "Sending…" : "Send Access Request"} type="submit" disabled={isSubmitting} />
                </div>
              </form>
            )}
          </SBCard>
          <SecondaryButton
            title={request.status === "open" ? "Mark as resolved" : "Reopen request"}
            onClick={() => void setResolved(request.status === "open")}
            disabled={isSubmitting}
          />
        </>
      )}
    </div>
  );
}

function NewSupportRequestForm({
  currentUser,
  onCreated,
  onCancel,
}: {
  currentUser: ReturnType<typeof useAuth>["currentUser"];
  onCreated: (request: SupportRequest) => void;
  onCancel: () => void;
}) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!currentUser || !subject.trim() || !message.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      onCreated(await createSupportRequest({
        subject: subject.trim(),
        message: message.trim(),
        name: preferredName(currentUser),
        email: currentUser.email,
      }));
    } catch (cause) {
      setError(errorMessage(cause, "The support request could not be sent."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SBCard>
      <form className="support-form" onSubmit={submit}>
        <div className="home-card-head"><h3>What can we help with?</h3></div>
        <FieldLabel label="Subject">
          <input
            value={subject}
            maxLength={SUPPORT_SUBJECT_MAX_LENGTH}
            onChange={(event) => setSubject(event.target.value)}
          />
        </FieldLabel>
        <FieldLabel label="Describe the issue">
          <textarea
            value={message}
            maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
            rows={7}
            onChange={(event) => setMessage(event.target.value)}
          />
        </FieldLabel>
        <div className="support-context">
          <span><strong>From</strong>{currentUser ? `${preferredName(currentUser)} · ${currentUser.email}` : "You"}</span>
          <span><strong>Workspace</strong>{currentUser?.activeContext?.displayName ?? "None"}</span>
        </div>
        <ErrorText>{error}</ErrorText>
        <div className="support-actions">
          <SecondaryButton title="Cancel" onClick={onCancel} disabled={isSubmitting} />
          <PrimaryButton
            title={isSubmitting ? "Sending…" : "Send Request"}
            type="submit"
            disabled={isSubmitting || !subject.trim() || !message.trim()}
          />
        </div>
      </form>
    </SBCard>
  );
}

function SupportRequestSection({
  title,
  requests,
  basePath,
  showRequester,
}: {
  title: string;
  requests: SupportRequest[];
  basePath: string;
  showRequester: boolean;
}) {
  if (requests.length === 0) return null;
  return (
    <section className="support-section">
      <h2>{title}</h2>
      <SBCard className="support-list">
        {requests.map((request) => (
          <Link key={request.id} className="event-row" to={`${basePath}/${encodeURIComponent(request.id)}`}>
            <div className="event-row-main">
              <strong>{request.subject}</strong>
              <span>
                {showRequester
                  ? `${request.name} · ${request.email}`
                  : request.messages.at(-1)?.body ?? ""}
              </span>
              <small>{formatDateTime(request.updatedAt)}</small>
            </div>
            <SupportStatusBadge status={request.status} />
          </Link>
        ))}
      </SBCard>
    </section>
  );
}

function SupportThreadCard({ request }: { request: SupportRequest }) {
  return (
    <SBCard className="support-thread">
      <div className="support-thread-head">
        <div>
          <h1>{request.subject}</h1>
          {request.workspace && <p className="home-hint">{workspaceSummary(request.workspace)}</p>}
        </div>
        <SupportStatusBadge status={request.status} />
      </div>
      <div className="support-messages">
        {request.messages.map((message) => (
          <article key={message.id} className={`support-message support-message-${message.authorKind}`}>
            <header>
              <strong>{message.authorName}</strong>
              <time dateTime={message.createdAt.toISOString()}>{formatDateTime(message.createdAt)}</time>
            </header>
            <p>{message.body}</p>
          </article>
        ))}
      </div>
    </SBCard>
  );
}

function SupportReplyComposer({
  label,
  value,
  setValue,
  onSubmit,
  disabled,
  buttonTitle,
}: {
  label: string;
  value: string;
  setValue: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  disabled: boolean;
  buttonTitle: string;
}) {
  return (
    <SBCard>
      <form className="support-form" onSubmit={onSubmit}>
        <FieldLabel label={label}>
          <textarea
            rows={5}
            value={value}
            maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
            onChange={(event) => setValue(event.target.value)}
          />
        </FieldLabel>
        <PrimaryButton title={disabled ? "Sending…" : buttonTitle} type="submit" disabled={disabled || !value.trim()} />
      </form>
    </SBCard>
  );
}

function PendingGrantCard({
  grant,
  disabled,
  onApprove,
  onDecline,
}: {
  grant: SupportAccessGrant;
  disabled: boolean;
  onApprove: () => void;
  onDecline: () => void;
}) {
  return (
    <SBCard className="home-card support-grant-card">
      <div className="home-card-head">
        <h3>Access Request</h3>
        <SupportStatusBadge status={grant.status} />
      </div>
      <p>
        {grant.adminName} from SBot support is asking for {grant.durationDays}-day access to{" "}
        {workspaceSummary(grant.workspace)}.
      </p>
      {grant.note && <blockquote>{grant.note}</blockquote>}
      <p className="home-hint">
        They will receive your role in this workspace, every change is recorded in Activity, and access ends automatically.
      </p>
      <div className="support-actions">
        <SecondaryButton title="Decline" destructive onClick={onDecline} disabled={disabled} />
        <PrimaryButton title="Approve" onClick={onApprove} disabled={disabled} />
      </div>
    </SBCard>
  );
}

function ActiveGrantCard({
  grant,
  disabled,
  onRevoke,
}: {
  grant: SupportAccessGrant;
  disabled: boolean;
  onRevoke: () => void;
}) {
  return (
    <SBCard className="home-card support-grant-card">
      <div className="home-card-head">
        <h3>Active Support Access</h3>
        <SupportStatusBadge status={grant.status} />
      </div>
      <p>{grant.adminName} has access to {workspaceSummary(grant.workspace)}.</p>
      {grant.expiresAt && <p className="home-hint">Ends {formatDateTime(grant.expiresAt)}</p>}
      <SecondaryButton title="Revoke access" destructive onClick={onRevoke} disabled={disabled} />
    </SBCard>
  );
}

function GrantStatusDetails({ grant }: { grant: SupportAccessGrant }) {
  const copy = {
    requested: `Waiting for the user to approve ${grant.durationDays}-day access.`,
    approved: `Access is active${grant.expiresAt ? ` until ${formatDateTime(grant.expiresAt)}` : ""}. Switch into the workspace from the workspace switcher.`,
    declined: "The user declined this access request. You can request again if it is still needed.",
    revoked: "The user revoked this access grant.",
    expired: "This access grant expired. You can request again if it is still needed.",
  }[grant.status];
  return <p className="home-hint">{copy}</p>;
}

function SupportStatusBadge({ status }: { status: SupportRequest["status"] | SupportAccessGrant["status"] }) {
  return <span className={`support-status support-status-${status}`}>{supportStatusLabel(status)}</span>;
}

function supportStatusLabel(status: SupportRequest["status"] | SupportAccessGrant["status"]): string {
  switch (status) {
    case "open": return "Open";
    case "resolved": return "Resolved";
    case "requested": return "Awaiting Approval";
    case "approved": return "Active";
    case "declined": return "Declined";
    case "revoked": return "Revoked";
    case "expired": return "Expired";
  }
}

function workspaceRoleLabel(workspace: SupportWorkspace): string {
  return USER_ROLE_LABELS[workspace.role as SBotUserRole] ?? workspace.role;
}

function workspaceSummary(workspace: SupportWorkspace): string {
  return `${workspace.displayName} · ${workspaceRoleLabel(workspace)}`;
}

function formatDateTime(value: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(value);
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}
